import { createModeratorApiClient } from '../utils/apiClient';
import { adminApiClient } from '../utils/apiClient';

const mod = createModeratorApiClient();

/**
 * Moderator scheduler — nested under /moderators/{id}/scheduler.
 * The moderator's numeric ID is required for all calls; read it from
 * useAuth().moderatorId in the calling component.
 */
export const moderatorSchedulerApi = {
  /**
   * @param {number} moderatorId
   * @param {import('axios').AxiosRequestConfig} [config]
   * @returns {Promise<import('axios').AxiosResponse<import('../types/api').ClientScheduler>>}
   */
  get: (moderatorId, config) =>
    mod.get(`/moderators/${moderatorId}/scheduler`, config),

  /**
   * @param {number} moderatorId
   * @param {{ is_enabled?: boolean; morning_time?: string; evening_time?: string; max_pages?: number }} body
   */
  put: (moderatorId, body, config) =>
    mod.put(`/moderators/${moderatorId}/scheduler`, body, config),

  /** Enable / disable without a full update. @param {number} moderatorId */
  toggle: (moderatorId, config) =>
    mod.post(`/moderators/${moderatorId}/scheduler/toggle`, null, config),

  /**
   * Immediately trigger a scrape cycle.
   * Quota-aware on the backend (auto/extra run limits, max URLs/articles per
   * client) — do not hardcode `maxPages` from the frontend; omit it so the
   * backend applies the client's configured scraper limits. May reject with
   * 403 `{ detail: { code, message, maxUrls? } }` when a quota is exceeded.
   * @param {number} moderatorId
   * @param {number} [maxPages] Explicit override — normally left undefined.
   */
  runNow: (moderatorId, maxPages, config = {}) =>
    mod.post(`/moderators/${moderatorId}/scheduler/run-now`, null, {
      ...config,
      params: { max_pages: maxPages, ...config.params },
    }),

  /** Generate AI newsletter drafts for all subscribers (admin-level trigger). */
  triggerDrafts: (config) =>
    mod.post('/moderators/scheduler/trigger-drafts', null, config),

  /**
   * Run only AI analysis steps (analyze-batch → analyze_topic_all) for today's
   * already-scraped articles, without re-scraping.
   * @param {number} moderatorId
   * @param {string} [analysisDate] YYYY-MM-DD, defaults to today on the server
   */
  runAnalysisOnly: (moderatorId, analysisDate, config = {}) =>
    mod.post(`/moderators/${moderatorId}/scheduler/run-analysis-only`, null, {
      ...config,
      params: { analysis_date: analysisDate, ...config.params },
    }),
};

/**
 * Admin view — all moderators' scheduler states.
 * Requires Admin JWT.
 */
export const adminSchedulerApi = {
  allStatuses: (config) =>
    adminApiClient.get('/client-schedulers/status/all', config),
};
